import React from 'react';
import { Avatar, Empty } from 'antd';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import {
  getActiveUserQuery, getAppUsageForDashboardQuery, getOpenDutiesQuery, getPendingReportsForDashboardQuery,
  getPendingTaskForDashboardQuery, getTotalDeployStaffQuery, getTotalguardingDutiesGraphQuery,
  getTotalguardingDutiesQuery, getTotalHourQuery,
} from '@src/util/query/dashboard.query';
import { MetricTile, PanelCard, Spark, useDashboard, useTicks } from './widgetShell';
import { RangeBadge, rangeVariables } from './widgetRange';
import { activeUserColumns, appUsageColumns, checkpointColumns, employeeColumns, pendingTaskColumns, scheduleColumns } from './dashboardDetailHost';

const sparkOf = (spark, field) => (spark || []).map((sp) => ({
  value: Number(sp?.[field] || 0).toFixed(2),
  label: dayjs(sp?.bucketStart).format('MMM YYYY'),
}));

export const ActiveUsersWidget = () => {
  const { openDetail } = useDashboard();
  const { data, loading } = useQuery(getActiveUserQuery, { fetchPolicy: 'network-only' });
  const active = data?.getActiveUser?.response?.active || 0;
  const users = data?.getActiveUser?.response?.users || 0;
  return (
    <MetricTile
      label="Active Users"
      value={loading && !data ? undefined : `${active} / ${users}`}
      loading={loading}
      onClick={() => openDetail('activeUser', { title: 'Active Users', columns: activeUserColumns, emptyText: 'No users found.' })}
    >
      {active > 0 && (
        <div className="avatar-group">
          <Avatar.Group maxCount={active} size="small">
            <div className="post-avatar"><Avatar size={25} style={{ backgroundColor: '#17a2b8' }} src="https://avatar.iran.liara.run/public/13" /></div>
          </Avatar.Group>
        </div>
      )}
    </MetricTile>
  );
};

// Open ("0"), Confirmed ("1", not completed yet) and Unconfirmed ("2", assigned but not confirmed) duties
const DUTY_STATUS = {
  '0': { label: 'Open duties', alert: true, empty: 'No open duties for this period.' },
  '1': { label: 'Confirmed duties', alert: false, empty: 'No confirmed duties for this period.' },
  '2': { label: 'Unconfirmed duties', alert: true, empty: 'No unconfirmed duties for this period.' },
};

export const DutyCountWidget = ({ status, settings, onSettings }) => {
  const { openDetail } = useDashboard();
  const meta = DUTY_STATUS[status];
  const { data, loading, refetch } = useQuery(getOpenDutiesQuery, {
    variables: { ...rangeVariables(settings), status },
    fetchPolicy: 'network-only',
  });
  useTicks(['duties'], refetch);
  const row = data?.getOpenDuties?.response?.[0];
  const count = row?.count || 0;
  return (
    <MetricTile
      label={meta.label}
      value={loading && !data ? undefined : count}
      loading={loading}
      alert={meta.alert && (status === '0' || count > 0)}
      badge={<RangeBadge settings={settings} onChange={onSettings} options={status === '1' ? ['today', 'weekly', 'monthly', 'custom', 'all'] : undefined} />}
      onClick={() => openDetail('schedule', { title: meta.label.replace(/^\w/, (c) => c.toUpperCase()), columns: scheduleColumns, emptyText: meta.empty, variables: { ids: row?.ids || [] } })}
    />
  );
};

export const GuardingDutiesWidget = ({ settings, onSettings }) => {
  const { openDetail } = useDashboard();
  const { data, loading, refetch } = useQuery(getTotalguardingDutiesQuery, { variables: rangeVariables(settings), fetchPolicy: 'network-only' });
  useTicks(['duties'], refetch);
  const response = data?.getTotalguardingDuties?.response;
  const row = response?.totalGuardingDuties?.[0];
  return (
    <MetricTile
      label="Guarding Duties"
      value={loading && !data ? undefined : Number(row?.count ?? 0).toFixed(0)}
      loading={loading}
      badge={<RangeBadge settings={settings} onChange={onSettings} />}
      onClick={() => openDetail('schedule', { title: 'Guarding Duties', columns: scheduleColumns, emptyText: 'No guarding duties for this period.', variables: { ids: row?.ids || [] } })}
    >
      <Spark data={sparkOf(response?.spark, 'count')} name="Total Guarding Duties" />
    </MetricTile>
  );
};

export const TotalHoursWidget = ({ settings, onSettings }) => {
  const { openDetail } = useDashboard();
  const { data, loading, refetch } = useQuery(getTotalHourQuery, { variables: rangeVariables(settings), fetchPolicy: 'network-only' });
  useTicks(['duties'], refetch);
  const response = data?.getTotalHour?.response;
  const row = response?.totalHours?.[0];
  return (
    <MetricTile
      label="Total Hours"
      value={loading && !data ? undefined : Number(row?.grandTotalHours ?? 0).toFixed(2)}
      loading={loading}
      badge={<RangeBadge settings={settings} onChange={onSettings} />}
      onClick={() => openDetail('schedule', { title: 'Total Hours', columns: scheduleColumns, emptyText: 'No duties for this period.', variables: { ids: row?.ids || [] } })}
    >
      <Spark data={sparkOf(response?.spark, 'totalHoursForBucket')} name="Total Hours" suffix=" hrs" />
    </MetricTile>
  );
};

export const DeployedStaffWidget = ({ settings, onSettings }) => {
  const { openDetail } = useDashboard();
  const { data, loading, refetch } = useQuery(getTotalDeployStaffQuery, { variables: rangeVariables(settings), fetchPolicy: 'network-only' });
  useTicks(['duties'], refetch);
  const response = data?.getTotalDeployStaff?.response;
  const row = response?.totalDeployedStaff?.[0];
  return (
    <MetricTile
      label="Deployed Staff"
      value={loading && !data ? undefined : Number(row?.count ?? 0).toFixed(0)}
      loading={loading}
      badge={<RangeBadge settings={settings} onChange={onSettings} />}
      onClick={() => openDetail('employee', { title: 'Deployed Staff', columns: employeeColumns, emptyText: 'No deployed staff for this period.', variables: { ids: row?.distinctEmployees || [] } })}
    >
      <Spark data={sparkOf(response?.spark, 'count')} name="Total Deployed Staff" />
    </MetricTile>
  );
};

export const PendingCheckpointsWidget = ({ settings, onSettings }) => {
  const { openDetail } = useDashboard();
  const { data, loading } = useQuery(getPendingReportsForDashboardQuery, { variables: rangeVariables(settings), fetchPolicy: 'network-only' });
  const response = data?.getPendingCheckpointsForDashboard?.response;
  // the aggregate behind this one returns {_id} docs rather than raw ids
  const ids = (response?.ids || []).map((id) => (id && typeof id === 'object' ? id._id : id));
  return (
    <MetricTile
      label="Pending Checkpoints"
      value={loading && !data ? undefined : (response?.count || 0)}
      loading={loading}
      alert
      badge={<RangeBadge settings={settings} onChange={onSettings} />}
      onClick={() => openDetail('pendingCheckpoint', { title: 'Pending Checkpoints', columns: checkpointColumns, emptyText: 'No pending checkpoints for this period.', variables: { ids } })}
    />
  );
};

export const PendingTasksWidget = ({ settings, onSettings }) => {
  const { openDetail } = useDashboard();
  const { data, loading } = useQuery(getPendingTaskForDashboardQuery, { variables: rangeVariables(settings), fetchPolicy: 'network-only' });
  const response = data?.getPendingTaskForDashboard?.response;
  return (
    <MetricTile
      label="Pending Tasks"
      value={loading && !data ? undefined : (response?.count || 0)}
      loading={loading}
      alert
      badge={<RangeBadge settings={settings} onChange={onSettings} />}
      onClick={() => openDetail('pendingTask', { title: 'Pending Tasks', columns: pendingTaskColumns, emptyText: 'No pending tasks for this period.', variables: { ids: response?.ids || [] } })}
    />
  );
};

// of the duties assigned to an employee this period, how many did they confirm / clock in via the app
export const AppUsageWidget = ({ settings, onSettings }) => {
  const { openDetail } = useDashboard();
  const { data, loading } = useQuery(getAppUsageForDashboardQuery, { variables: rangeVariables(settings), fetchPolicy: 'network-only' });
  const response = data?.getAppUsageForDashboard?.response;
  return (
    <MetricTile
      label="App Usage"
      value={loading && !data ? undefined : `${response?.usedCount || 0} / ${response?.total || 0}`}
      loading={loading}
      badge={<RangeBadge settings={settings} onChange={onSettings} />}
      onClick={() => openDetail('schedule', { title: 'App Usage', columns: appUsageColumns, emptyText: 'No shifts assigned for this period.', variables: { ids: response?.ids || [] } })}
    />
  );
};

export const GuardingHoursChartWidget = ({ settings, onSettings }) => {
  const range = settings?.range || 'yearly';
  const { data, loading } = useQuery(getTotalguardingDutiesGraphQuery, {
    variables: { ...rangeVariables(settings, 'yearly'), graphType: range },
    fetchPolicy: 'network-only',
  });
  const points = data?.getTotalguardingDutiesGraph?.response || [];
  return (
    <PanelCard
      title="Guarding Hours Activity"
      extra={<RangeBadge settings={settings} onChange={onSettings} fallback="yearly" options={['weekly', 'monthly', 'yearly', 'custom']} />}
    >
      {points.length > 0 ? (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={points}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
            <XAxis dataKey="month" tick={{ fill: '#8c8c8c', fontSize: 11 }} />
            <YAxis tick={{ fill: '#8c8c8c', fontSize: 11 }} />
            <Tooltip contentStyle={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '12px' }} />
            <Bar dataKey="hours" fill="#17a2b8" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="dw-empty">
          <Empty description={loading ? 'Loading…' : 'No guarding hours data for this period.'} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      )}
    </PanelCard>
  );
};
